/**
 * Extracts a user-friendly error message from an axios error or any error object.
 *
 * Priority chain:
 *   1. 504 → fixed timeout message (Cloudflare returns HTML, not JSON)
 *   2. error.response.data.message → the API's error message
 *   3. "Request failed with status {code} (METHOD /route)" → fallback for non-JSON responses
 *   4. Network error → friendly connectivity message
 *   5. error.message → generic errors (e.g. Auth0)
 *   6. Default fallback (non-axios errors without a message)
 */
export const extractApiErrorMessage = (error) => {
  // Axios error with a server response
  if (error.response) {
    const { data, status } = error.response;

    // Cloudflare gateway timeout returns HTML, not JSON
    if (status === 504) {
      return 'The request timed out. Please try again.';
    }

    // The API always returns { message: "..." } in error responses
    if (typeof data?.message === 'string') {
      return data.message;
    }

    const method = error.config?.method?.toUpperCase();
    const route = error.config?.url;
    return `Request failed with status ${status}${method && route ? ` (${method} ${route})` : ''}`;
  }

  // Network error (when network or server is down)
  if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
    return 'Unable to reach the server. Please check your internet connection and try again.';
  }

  // Non-axios errors
  if (error.message) {
    return error.message;
  }

  // if this error message is reached, we should add additional handling in this function to extract more specific error messages
  console.error(error);
  return 'An unexpected error occurred';
};
